import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import { Button, CircularProgress, Menu, MenuItem, Tooltip } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../containers/ApiContainer";
import { useHangarsState } from "../../containers/HangarsStateContainer";
import { useRampsState } from "../../containers/RampsStateContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { PlacementService } from "../../services/PlacementService";
import { TripWithTenant } from "../../types";
import { SplitButton } from "../SplitButton/SplitButton";

interface TripWithPlacement extends TripWithTenant {
  hangar_id?: string | null;
  ramp_id?: string | null;
  hangar_name?: string | null;
  ramp_name?: string | null;
  location_name?: string | null;
  location_type?: string | null;
}

interface AssignTripButtonProps {
  trip: TripWithPlacement;
  currentLocationId: string;
  currentLocationType: "hangar" | "ramp";
  isAssigned?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  iconOnly?: boolean;
  onAssignmentComplete?: () => void;
  // Bulk assignment props (optional)
  selectedTripIds?: string[];
  allTrips?: TripWithPlacement[];
  onBulkAssignComplete?: () => void;
}

export const AssignTripButton: React.FC<AssignTripButtonProps> = ({
  trip,
  currentLocationId,
  currentLocationType,
  isAssigned = false,
  disabled = false,
  size = "small",
  iconOnly = false,
  onAssignmentComplete,
  selectedTripIds,
  allTrips,
  onBulkAssignComplete,
}) => {
  // Check if this is a bulk operation (multiple trips selected and this row is one of them)
  const isBulkMode = Boolean(selectedTripIds && selectedTripIds.length > 1 && allTrips);
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const { hangars } = useHangarsState();
  const { ramps } = useRampsState();
  const snackbar = useSnackbar();
  const [assigning, setAssigning] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const placementService = React.useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );

  // Handle assignment to any location (single or bulk mode)
  const handleAssignToLocation = async (
    locationId: string,
    locationType: "hangar" | "ramp",
    locationName: string
  ) => {
    try {
      setAssigning(true);

      if (isBulkMode && selectedTripIds && allTrips) {
        // Bulk assignment mode - assign all selected trips
        const tripsToAssign = allTrips.filter((t) =>
          selectedTripIds.includes(t.trip_id)
        );

        await Promise.all(
          tripsToAssign.map((tripItem) =>
            placementService.assignToLocationWithGapFilling(
              tripItem.trip_id,
              locationId,
              locationType,
              tripItem.arrival_date || "",
              tripItem.departure_date || "",
              true,
              { x: null, y: null, angle: null }
            )
          )
        );

        snackbar.notify({
          text: t('widgets.assignTripButton.bulkSuccessMessage', {
            count: tripsToAssign.length,
            locationName,
          }),
          severity: "success",
        });

        if (onBulkAssignComplete) {
          onBulkAssignComplete();
        }
      } else {
        // Single trip assignment mode
        await placementService.assignToLocationWithGapFilling(
          trip.trip_id,
          locationId,
          locationType,
          trip.arrival_date || "",
          trip.departure_date || "",
          true,
          { x: null, y: null, angle: null }
        );

        snackbar.notify({
          text: t('widgets.assignTripButton.successMessage', {
            tailNumber: trip.tail_number,
            locationName,
          }),
          severity: "success",
        });

        if (onAssignmentComplete) {
          onAssignmentComplete();
        }
      }
    } catch (error: any) {
      console.error("Failed to assign trip:", error);
      snackbar.notify({
        text: error.message || t('widgets.assignTripButton.errorMessage'),
        severity: "error",
      });
    } finally {
      setAssigning(false);
    }
  };

  // Build options for the split button
  const options = React.useMemo(() => {
    const allOptions = [];

    // First option: current location (default)
    const currentLocationName =
      currentLocationType === "hangar"
        ? hangars.find((h) => h.id === currentLocationId)?.name || t('widgets.assignTripButton.thisHangar')
        : ramps.find((r) => r.id === currentLocationId)?.name || t('widgets.assignTripButton.thisRamp');

    allOptions.push({
      label: isAssigned ? t('widgets.assignTripButton.assigned') : t('widgets.assignTripButton.assignTo', { locationName: currentLocationName }),
      disabled: isAssigned || disabled,
      onClick: () =>
        handleAssignToLocation(
          currentLocationId,
          currentLocationType,
          currentLocationName
        ),
    });

    // Add separator label if there are other locations
    if (hangars.length > 1 || ramps.length > 0) {
      // Add all other hangars
      const otherHangars = hangars.filter(
        (hangar) =>
          hangar.id !== currentLocationId || currentLocationType !== "hangar"
      );

      if (otherHangars.length > 0) {
        otherHangars.forEach((hangar) => {
          const isAssignedToThis = trip.hangar_id === hangar.id;
          allOptions.push({
            label: isAssignedToThis
              ? `✓ ${hangar.name}`
              : hangar.name,
            disabled: isAssignedToThis || disabled,
            onClick: () =>
              handleAssignToLocation(hangar.id, "hangar", hangar.name),
          });
        });
      }

      // Add all ramps
      const otherRamps = ramps.filter(
        (ramp) =>
          ramp.id !== currentLocationId || currentLocationType !== "ramp"
      );

      if (otherRamps.length > 0) {
        otherRamps.forEach((ramp) => {
          const isAssignedToThis = trip.ramp_id === ramp.id;
          allOptions.push({
            label: isAssignedToThis
              ? `✓ ${ramp.name}`
              : ramp.name,
            disabled: isAssignedToThis || disabled,
            onClick: () =>
              handleAssignToLocation(ramp.id, "ramp", ramp.name),
          });
        });
      }
    }

    return allOptions;
  }, [
    t,
    hangars,
    ramps,
    currentLocationId,
    currentLocationType,
    isAssigned,
    disabled,
    trip.hangar_id,
    trip.ramp_id,
    trip.tail_number,
    trip.trip_id,
    trip.arrival_date,
    trip.departure_date,
  ]);

  // Show loading spinner if assigning
  if (assigning) {
    return <CircularProgress size={size === "small" ? 12 : 20} />;
  }

  // Get current location name for tooltip
  const currentLocationName =
    currentLocationType === "hangar"
      ? hangars.find((h) => h.id === currentLocationId)?.name || t('widgets.assignTripButton.thisHangar')
      : ramps.find((r) => r.id === currentLocationId)?.name || t('widgets.assignTripButton.thisRamp');

  // Icon-only mode: show button with icon and text, dropdown menu on click
  if (iconOnly) {
    const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
      if (!disabled && !isAssigned) {
        setMenuAnchorEl(event.currentTarget);
      }
    };

    const handleMenuClose = () => {
      setMenuAnchorEl(null);
    };

    // Determine tooltip and button text based on mode
    const bulkCount = selectedTripIds?.length || 0;
    const tooltipTitle = isAssigned
      ? t('widgets.assignTripButton.assignedTo', { locationName: currentLocationName })
      : isBulkMode
      ? t('widgets.assignTripButton.assignSelected', { count: bulkCount })
      : t('widgets.assignTripButton.assignTo', { locationName: currentLocationName });

    const buttonText = isAssigned
      ? t('widgets.assignTripButton.assigned')
      : isBulkMode
      ? t('widgets.assignTripButton.assignCount', { count: bulkCount })
      : t('widgets.assignTripButton.assign');

    return (
      <>
        <Tooltip title={tooltipTitle}>
          <span>
            <Button
              startIcon={isAssigned ? <CheckCircleIcon /> : <AddLocationAltIcon />}
              onClick={handleButtonClick}
              disabled={disabled}
              size={size}
              variant={isAssigned ? "text" : "outlined"}
              color={isAssigned ? "success" : isBulkMode ? "secondary" : "primary"}
              sx={{
                minHeight: 0,
                py: 0.25,
                px: 1,
              }}
            >
              {buttonText}
            </Button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {options.map((option, index) => (
            <MenuItem
              key={index}
              disabled={option.disabled}
              onClick={() => {
                if (!option.disabled && option.onClick) {
                  option.onClick();
                }
                handleMenuClose();
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  // Standard mode: show split button
  return (
    <SplitButton
      options={options}
      disabled={disabled || assigning}
      color={isAssigned ? "inherit" : "primary"}
      size={size}
    />
  );
};